import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import { getOddsFormatIs } from '@cx-web/utils';

import { useOddsUtils } from '~/hooks/use-odds-utils';
import { type OddsFormat } from '~/types';
import { type OddsFormatIs } from '~/types/odds-format';

type OddsFormatContextValue = {
  changeOddsFormat: (newOddsFormat: OddsFormat) => void;
  oddsFormatIs: OddsFormatIs;
  userOddsFormat: OddsFormat;
};

const OddsFormatContext = createContext<OddsFormatContextValue>(undefined as any as OddsFormatContextValue);
OddsFormatContext.displayName = 'OddsFormatContext';

export const OddsFormatProvider = ({ children, userOddsFormat }: PropsWithChildren<{ userOddsFormat: OddsFormat }>) => {
  const { changeOddsFormat } = useOddsUtils();
  const oddsFormatIs: OddsFormatIs = useMemo(() => getOddsFormatIs(userOddsFormat), [userOddsFormat]);

  const value = useMemo(
    () => ({ changeOddsFormat, oddsFormatIs, userOddsFormat }),
    [changeOddsFormat, oddsFormatIs, userOddsFormat],
  );

  return <OddsFormatContext.Provider value={value}>{children}</OddsFormatContext.Provider>;
};

export const useOddsFormatContext = () => {
  const context = useContext(OddsFormatContext);
  if (!context) {
    throw new Error('useOddsFormatContext can only be used inside of OddsFormatProvider');
  }

  return context;
};
